import { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Plus, Trash2, Pencil, GripVertical, X,
  Globe, Mail, Phone, MapPin, Music, Video,
  Twitter, Linkedin, Github, Instagram, Youtube, Facebook,
  ExternalLink, MessageCircle, Briefcase, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canAddLink, getMaxLinks } from "@/lib/entitlements";

/* ───── Content Type Definitions ───── */
const CONTENT_TYPES = [
  {
    type: 'website',
    label: 'Website Link',
    icon: Globe,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    description: 'Link to any website or webpage',
    defaultIcon: 'globe',
  },
  {
    type: 'social',
    label: 'Social Profile',
    icon: Twitter,
    color: 'text-violet-500',
    bg: 'bg-violet-500/10',
    description: 'Link to a social media profile',
    defaultIcon: 'twitter',
  },
  {
    type: 'email',
    label: 'Email',
    icon: Mail,
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
    description: 'Let visitors send you an email',
    defaultIcon: 'mail',
  },
  {
    type: 'phone',
    label: 'Phone',
    icon: Phone,
    color: 'text-green-500',
    bg: 'bg-green-500/10',
    description: 'One-tap phone call button',
    defaultIcon: 'phone',
  },
  {
    type: 'music',
    label: 'Music / Audio',
    icon: Music,
    color: 'text-pink-500',
    bg: 'bg-pink-500/10',
    description: 'Link to a song, playlist, or podcast',
    defaultIcon: 'music',
  },
  {
    type: 'video',
    label: 'Video',
    icon: Video,
    color: 'text-red-500',
    bg: 'bg-red-500/10',
    description: 'Link to a YouTube, Vimeo, or other video',
    defaultIcon: 'video',
  },
  {
    type: 'location',
    label: 'Location',
    icon: MapPin,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
    description: 'Share your location or office address',
    defaultIcon: 'map-pin',
  },
  {
    type: 'custom',
    label: 'Custom Link',
    icon: ExternalLink,
    color: 'text-muted-foreground',
    bg: 'bg-secondary',
    description: 'Any other type of link',
    defaultIcon: 'external-link',
  },
];

const SOCIAL_PLATFORMS = [
  { value: 'twitter', label: 'Twitter / X', icon: 'twitter' },
  { value: 'linkedin', label: 'LinkedIn', icon: 'linkedin' },
  { value: 'instagram', label: 'Instagram', icon: 'instagram' },
  { value: 'github', label: 'GitHub', icon: 'github' },
  { value: 'youtube', label: 'YouTube', icon: 'youtube' },
  { value: 'facebook', label: 'Facebook', icon: 'facebook' },
  { value: 'whatsapp', label: 'WhatsApp', icon: 'whatsapp' },
  { value: 'other', label: 'Other', icon: 'external-link' },
];

const ICON_MAP = {
  globe: Globe, mail: Mail, phone: Phone, 'map-pin': MapPin,
  music: Music, video: Video, twitter: Twitter, linkedin: Linkedin,
  github: Github, instagram: Instagram, youtube: Youtube, facebook: Facebook,
  'external-link': ExternalLink, whatsapp: MessageCircle, message: MessageCircle,
  briefcase: Briefcase, 'file-text': FileText,
};

function LinkIcon({ name, ...props }) {
  const Icon = ICON_MAP[name] || ExternalLink;
  return <Icon {...props} />;
}

function getTypeConfig(linkType) {
  return CONTENT_TYPES.find((t) => t.type === linkType) || CONTENT_TYPES[CONTENT_TYPES.length - 1];
}

/* ───── Type-Specific Form ───── */
function TypeSpecificForm({ type, formData, setFormData }) {
  switch (type) {
    case 'website':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Button Label</Label>
            <Input value={formData.label} onChange={(e) => setFormData((p) => ({ ...p, label: e.target.value }))} placeholder="Visit My Website" data-testid="form-label-input" />
            <p className="text-xs text-muted-foreground">Text displayed on the link button</p>
          </div>
          <div className="space-y-2">
            <Label>Website URL</Label>
            <Input value={formData.url} onChange={(e) => setFormData((p) => ({ ...p, url: e.target.value }))} placeholder="https://yourwebsite.com" data-testid="form-url-input" />
          </div>
        </div>
      );
    case 'social':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Platform</Label>
            <Select
              value={formData.platform || 'twitter'}
              onValueChange={(v) => {
                const plat = SOCIAL_PLATFORMS.find((p) => p.value === v);
                setFormData((p) => ({ ...p, platform: v, icon: plat?.icon || 'external-link', label: p.label || plat?.label || '' }));
              }}
            >
              <SelectTrigger data-testid="form-platform-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOCIAL_PLATFORMS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    <span className="flex items-center gap-2"><LinkIcon name={p.icon} size={14} /> {p.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Profile URL</Label>
            <Input value={formData.url} onChange={(e) => setFormData((p) => ({ ...p, url: e.target.value }))} placeholder="https://twitter.com/yourhandle" data-testid="form-url-input" />
          </div>
          <div className="space-y-2">
            <Label>Custom Label <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Input value={formData.label} onChange={(e) => setFormData((p) => ({ ...p, label: e.target.value }))} placeholder="Follow me on X" data-testid="form-label-input" />
            <p className="text-xs text-muted-foreground">Leave empty to use the platform name</p>
          </div>
        </div>
      );
    case 'email':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Email Address</Label>
            <Input value={formData.rawValue || ''} onChange={(e) => setFormData((p) => ({ ...p, rawValue: e.target.value, url: `mailto:${e.target.value}` }))} placeholder="hello@yourname.com" type="email" data-testid="form-email-input" />
          </div>
          <div className="space-y-2">
            <Label>Button Label</Label>
            <Input value={formData.label} onChange={(e) => setFormData((p) => ({ ...p, label: e.target.value }))} placeholder="Send me an email" data-testid="form-label-input" />
          </div>
        </div>
      );
    case 'phone':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Phone Number</Label>
            <Input value={formData.rawValue || ''} onChange={(e) => setFormData((p) => ({ ...p, rawValue: e.target.value, url: `tel:${e.target.value.replace(/\s/g, '')}` }))} placeholder="+1 (555) 123-4567" data-testid="form-phone-input" />
          </div>
          <div className="space-y-2">
            <Label>Button Label</Label>
            <Input value={formData.label} onChange={(e) => setFormData((p) => ({ ...p, label: e.target.value }))} placeholder="Call me" data-testid="form-label-input" />
          </div>
        </div>
      );
    case 'music':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Track / Playlist Title</Label>
            <Input value={formData.label} onChange={(e) => setFormData((p) => ({ ...p, label: e.target.value }))} placeholder="My Latest Track" data-testid="form-label-input" />
          </div>
          <div className="space-y-2">
            <Label>Audio / Streaming URL</Label>
            <Input value={formData.url} onChange={(e) => setFormData((p) => ({ ...p, url: e.target.value }))} placeholder="https://open.spotify.com/track/..." data-testid="form-url-input" />
            <p className="text-xs text-muted-foreground">Spotify, Apple Music, SoundCloud, or direct audio URL</p>
          </div>
        </div>
      );
    case 'video':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Video Title</Label>
            <Input value={formData.label} onChange={(e) => setFormData((p) => ({ ...p, label: e.target.value }))} placeholder="Watch My Latest Video" data-testid="form-label-input" />
          </div>
          <div className="space-y-2">
            <Label>Video URL</Label>
            <Input value={formData.url} onChange={(e) => setFormData((p) => ({ ...p, url: e.target.value }))} placeholder="https://youtube.com/watch?v=..." data-testid="form-url-input" />
            <p className="text-xs text-muted-foreground">YouTube, Vimeo, TikTok, or other video URL</p>
          </div>
        </div>
      );
    case 'location':
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Place Name</Label>
            <Input value={formData.label} onChange={(e) => setFormData((p) => ({ ...p, label: e.target.value }))} placeholder="Our Office" data-testid="form-label-input" />
          </div>
          <div className="space-y-2">
            <Label>Map / Address URL</Label>
            <Input value={formData.url} onChange={(e) => setFormData((p) => ({ ...p, url: e.target.value }))} placeholder="https://maps.google.com/?q=..." data-testid="form-url-input" />
            <p className="text-xs text-muted-foreground">Google Maps link or any address URL</p>
          </div>
        </div>
      );
    default: // custom
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Link Label</Label>
            <Input value={formData.label} onChange={(e) => setFormData((p) => ({ ...p, label: e.target.value }))} placeholder="My Link" data-testid="form-label-input" />
          </div>
          <div className="space-y-2">
            <Label>URL</Label>
            <Input value={formData.url} onChange={(e) => setFormData((p) => ({ ...p, url: e.target.value }))} placeholder="https://..." data-testid="form-url-input" />
          </div>
          <div className="space-y-2">
            <Label>Icon</Label>
            <Select value={formData.icon} onValueChange={(v) => setFormData((p) => ({ ...p, icon: v }))}>
              <SelectTrigger data-testid="form-icon-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ICON_MAP).map(([key, Icon]) => (
                  <SelectItem key={key} value={key}>
                    <span className="flex items-center gap-2"><Icon size={14} /> {key}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      );
  }
}

/* ───── Main Component ───── */
export default function LinkManager() {
  const { entitlements, entitlementsReady, authLoading } = useAuth();
  const maxLinks = getMaxLinks(entitlements);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({ label: '', url: '', icon: 'globe', platform: '', rawValue: '' });
  const [adding, setAdding] = useState(false);
  const [editLink, setEditLink] = useState(null);
  const [saving, setSaving] = useState(false);
  const canAddMore = entitlementsReady && canAddLink(entitlements, links.length);

  const loadLinks = async () => {
    try {
      const res = await api.get("/links");
      setLinks(res.data || []);
    } catch {} finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLinks(); }, []);

  const resetForm = () => {
    setSelectedType(null);
    setFormData({ label: '', url: '', icon: 'globe', platform: '', rawValue: '' });
    setAddOpen(false);
  };

  const selectType = (type) => {
    const config = getTypeConfig(type);
    setSelectedType(type);
    setFormData({ label: '', url: '', icon: config.defaultIcon, platform: '', rawValue: '' });
  };

  const handleAdd = async () => {
    if (!canAddMore) {
      toast.error("Your plan's link limit has been reached. Upgrade to add more links.");
      return;
    }
    if (!formData.label.trim() && selectedType === 'social') {
      const plat = SOCIAL_PLATFORMS.find((p) => p.value === formData.platform);
      formData.label = plat?.label || 'Social';
    }
    if (!formData.label.trim() || !formData.url.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setAdding(true);
    try {
      await api.post("/links", {
        label: formData.label,
        url: formData.url,
        icon: formData.icon,
        link_type: selectedType || 'custom',
      });
      resetForm();
      await loadLinks();
      toast.success("Content added!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (linkId) => {
    try {
      await api.delete(`/links/${linkId}`);
      await loadLinks();
      toast.success("Removed");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const reordered = Array.from(links);
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setLinks(reordered);
    try {
      await api.put("/links/reorder", { link_ids: reordered.map((l) => l.id) });
    } catch {
      loadLinks();
    }
  };

  const openEdit = (link) => {
    const linkType = link.link_type || guessType(link);
    setEditLink(link);
    setSelectedType(linkType);
    const raw = link.url.startsWith('mailto:') ? link.url.replace('mailto:', '') : link.url.startsWith('tel:') ? link.url.replace('tel:', '') : '';
    setFormData({
      label: link.label,
      url: link.url,
      icon: link.icon || 'external-link',
      platform: link.icon || '',
      rawValue: raw,
    });
  };

  const guessType = (link) => {
    if (link.url?.startsWith('mailto:')) return 'email';
    if (link.url?.startsWith('tel:')) return 'phone';
    if (['twitter', 'linkedin', 'instagram', 'github', 'youtube', 'facebook', 'whatsapp'].includes(link.icon)) return 'social';
    if (link.icon === 'music') return 'music';
    if (link.icon === 'video') return 'video';
    if (link.icon === 'map-pin') return 'location';
    return 'website';
  };

  const handleEdit = async () => {
    if (!formData.label.trim() || !formData.url.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/links/${editLink.id}`, {
        label: formData.label,
        url: formData.url,
        icon: formData.icon,
        link_type: selectedType || 'custom',
      });
      setEditLink(null);
      resetForm();
      await loadLinks();
      toast.success("Updated!");
    } catch {
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="auth-loading-shell">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-8 max-w-2xl" data-testid="link-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Content</h1>
          <p className="text-muted-foreground text-sm">
            Add links, contact info, and media to your Smart-QR page
            {maxLinks != null && maxLinks >= 0 ? ` (${links.length}/${maxLinks})` : maxLinks != null && maxLinks < 0 ? "" : ""}
          </p>
        </div>
        <Button className="btn-glow gap-2" onClick={() => setAddOpen(true)} disabled={!canAddMore} data-testid="add-content-btn">
          <Plus size={16} /> Add
        </Button>
      </div>

      {/* Links list */}
      <div data-testid="links-list">
        {links.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Globe className="mx-auto text-muted-foreground mb-4" size={40} />
              <h3 className="font-heading text-lg font-semibold mb-1">No content yet</h3>
              <p className="text-muted-foreground text-sm mb-6">Add your first link, social profile, or contact info</p>
              <Button onClick={() => setAddOpen(true)} className="btn-glow gap-2" data-testid="add-first-content-btn">
                <Plus size={16} /> Add Content
              </Button>
            </CardContent>
          </Card>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="links-droppable" isDropDisabled={false} isCombineEnabled={false}>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2.5">
                  {links.map((link, idx) => {
                    const typeConfig = getTypeConfig(link.link_type || guessType(link));
                    return (
                      <Draggable key={link.id} draggableId={link.id} index={idx}>
                        {(prov, snapshot) => (
                          <div ref={prov.innerRef} {...prov.draggableProps} data-testid={`link-card-${link.id}`}>
                            <Card className={`card-hover transition-shadow ${snapshot.isDragging ? 'shadow-lg ring-1 ring-primary/20' : ''}`}>
                              <CardContent className="flex items-center gap-3 p-3.5">
                                <div {...prov.dragHandleProps} className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors shrink-0 touch-none" data-testid={`drag-handle-${idx}`}>
                                  <GripVertical size={16} />
                                </div>
                                <div className={`p-2 rounded-lg shrink-0 ${typeConfig.bg}`}>
                                  <LinkIcon name={link.icon} size={18} className={typeConfig.color} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-sm truncate">{link.label}</p>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground shrink-0">{typeConfig.label}</span>
                                  </div>
                                  <p className="text-xs text-muted-foreground truncate mt-0.5">{link.url}</p>
                                </div>
                                <div className="flex items-center gap-0.5 shrink-0">
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(link)} data-testid={`edit-link-${idx}`}>
                                    <Pencil size={14} />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(link.id)} data-testid={`delete-link-${idx}`}>
                                    <Trash2 size={14} />
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      {/* Add Content Dialog */}
      <Dialog open={addOpen} onOpenChange={(open) => { if (!open) resetForm(); else setAddOpen(true); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedType ? (
                <span className="flex items-center gap-2">
                  {(() => { const C = getTypeConfig(selectedType); return <C.icon size={20} className={C.color} />; })()}
                  Add {getTypeConfig(selectedType).label}
                </span>
              ) : 'Add Content'}
            </DialogTitle>
          </DialogHeader>

          {!selectedType ? (
            <div className="grid grid-cols-2 gap-3 py-2" data-testid="content-type-grid">
              {CONTENT_TYPES.map((ct) => (
                <button
                  key={ct.type}
                  onClick={() => selectType(ct.type)}
                  className="flex items-start gap-3 p-3.5 rounded-xl border border-border hover:border-primary/30 bg-card hover:bg-secondary/40 transition-all text-left"
                  data-testid={`type-${ct.type}`}
                >
                  <div className={`p-2 rounded-lg shrink-0 ${ct.bg}`}>
                    <ct.icon size={18} className={ct.color} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{ct.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{ct.description}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-5 py-2">
              <button onClick={() => setSelectedType(null)} className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1" data-testid="back-to-types">
                <X size={12} /> Change type
              </button>
              <TypeSpecificForm type={selectedType} formData={formData} setFormData={setFormData} />
              <Button onClick={handleAdd} disabled={adding} className="w-full btn-glow" data-testid="submit-content-btn">
                {adding ? "Adding..." : `Add ${getTypeConfig(selectedType).label}`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editLink} onOpenChange={(open) => { if (!open) { setEditLink(null); resetForm(); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedType && (
                <span className="flex items-center gap-2">
                  {(() => { const C = getTypeConfig(selectedType); return <C.icon size={20} className={C.color} />; })()}
                  Edit {getTypeConfig(selectedType).label}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <TypeSpecificForm type={selectedType || 'custom'} formData={formData} setFormData={setFormData} />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setEditLink(null); resetForm(); }}>Cancel</Button>
            <Button onClick={handleEdit} disabled={saving} className="btn-glow" data-testid="save-edit-btn">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
